import React, { useEffect, useRef, useState } from "react";
import { useGetEditor } from "../../context/useEditorContext";
import { useTheme } from "../../context/useThemeContext";
import { hexToRGBA } from "../../utility/hexToRGB";
import ToggleIconButton from "../../components/buttons/toggleIconButton";
import { FaLink, FaLinkSlash } from "react-icons/fa6";
import { TextSelection } from "@tiptap/pm/state";

import { Editor, getMarkRange } from "@tiptap/react";
import useAbsoluteWindowStore from "../../stores/absoluteWindowStore";

function ManageLinkHover() {
    const { theme } = useTheme();

    const [hoveredLink, setHoveredLink] = useState<{
        href: string;
        x: number;
        y: number;
    } | null>(null);
    const [hoveredLinkElem, setHoveredLinkElem] = useState<HTMLElement | null>(
        null
    );

    const [divWidth, setDivWidth] = useState(0);
    const divRef = useRef<HTMLDivElement>(null);

    const editor = useGetEditor().editor.current;

    useEffect(() => {
        const handleMouseOver = (event: Event) => {
            const elem = event.target as HTMLElement;
            if (elem.tagName === "A") {
                const href = elem.getAttribute("href");
                const fontSize = parseFloat(
                    window.getComputedStyle(elem).fontSize
                );
                const y = elem.offsetTop + fontSize;
                const x = elem.offsetLeft + elem.offsetWidth / 2;
                if (!href) return;
                setHoveredLink({ href, y, x });
                setHoveredLinkElem(elem);
            } else {
                setHoveredLink(null);
                setHoveredLinkElem(null);
            }
        };

        editor?.view.dom.addEventListener("mouseover", handleMouseOver);

        return () => {
            editor?.view.dom.removeEventListener("mouseover", handleMouseOver);
        };
    }, []);

    // Set width
    useEffect(() => {
        const observer = new ResizeObserver((entries) => {
            console.log("resized");

            if (entries[0]) {
                setDivWidth(entries[0].contentRect.width);
            }
        });
        if (divRef.current) {
            observer.observe(divRef.current);
        }
        return () => {
            if (divRef.current) {
                observer.unobserve(divRef.current);
            }
        };
    }, [divRef, hoveredLink]);

    return (
        <>
            {hoveredLink && (
                <div
                    ref={divRef}
                    className="absolute"
                    style={{
                        //? A bit hacky, but it works
                        left: Math.max(
                            5,
                            Math.min(
                                hoveredLink.x - divWidth / 2,
                                window.innerWidth - divWidth - 20
                            )
                        ),
                        top: hoveredLink.y,
                        paddingTop: "5px",
                        // Necessary to prevent flicker on show
                        opacity: divWidth > 0 ? 1 : 0,
                        transition: "opacity 0.1s ease-in-out",
                        display: hoveredLink ? "flex" : "none",
                    }}
                >
                    <div
                        className=" gap-2 flex  items-center justify-center z-[999]"
                        style={{
                            padding: "4px 4px",
                            borderRadius: "8px",
                            backgroundColor: hexToRGBA(
                                theme.mainContentColor,
                                0.8
                            ), // theme.mainContentColor,
                            border: `1px solid  ${hexToRGBA(
                                theme.fontColor[100],
                                0.2
                            )}`,
                            // height: "40px",
                            backdropFilter: "blur(5px)",
                        }}
                    >
                        <ToggleIconButton
                            alt="Edit link"
                            size={{ x: 24, y: 24 }}
                            icon={
                                <FaLink
                                    size={16}
                                    color={theme.fontColor[100]}
                                />
                            }
                            onClick={(e) => {
                                SelectElement(editor, hoveredLinkElem);
                                useAbsoluteWindowStore
                                    .getState()
                                    .showTextMenu(editor);
                            }}
                            className="flex-grow-0"
                            hoverBGInactive={theme.headerColor}
                        />
                        <ToggleIconButton
                            alt="Unset link"
                            size={{ x: 24, y: 24 }}
                            icon={
                                <FaLinkSlash
                                    size={16}
                                    color={theme.fontColor[100]}
                                />
                            }
                            onClick={(e) => {
                                SelectElement(editor, hoveredLinkElem);
                                editor?.commands.unsetLink();
                            }}
                            className="flex-grow-0"
                            hoverBGInactive={theme.headerColor}
                        />
                        <a
                            href={hoveredLink.href}
                            target="_blank"
                            rel="noreferrer"
                            className="text-sm max-w-[250px] overflow-hidden text-ellipsis pr-1 text-nowrap p-[2px] rounded-sm"
                            color={theme.fontColor[100]}
                        >
                            {/* https://www.youtube.com/watch?v=KgfiIB-Mzaw */}
                            {hoveredLink.href.replace(/^[^:]+:\/\/www./, "")}
                        </a>
                    </div>
                </div>
            )}
        </>
    );
}

export default ManageLinkHover;

function SelectElement(editor: Editor | null, hoveredLinkElem: Node | null) {
    if (!editor || !hoveredLinkElem) return;

    // Ensure the type is correct
    if (!(hoveredLinkElem instanceof Node)) return;

    const pos = editor.view.posAtDOM(hoveredLinkElem, 0);

    if (typeof pos === "number") {
        const node = editor.state.doc.nodeAt(pos);
        const nodeSize = node?.nodeSize || 1;

        const tr = editor.state.tr.setSelection(
            // @ts-ignore
            editor.state.selection.constructor.create(
                editor.state.doc,
                pos,
                pos + nodeSize
            )
        );

        editor.view.dispatch(tr);
        editor.view.focus();
    }
}
